import type { ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { SpanishTooltip } from '../../components/charts/SpanishTooltip';
import type { MgmtReviewInputs } from './types';

const COLORS = ['#2563eb', '#16a34a', '#dc2626', '#ca8a04', '#7c3aed'];

interface InputsSummaryChartsProps {
  inputs: MgmtReviewInputs;
}

function toChartData(record: Record<string, number>) {
  return Object.entries(record).map(([name, value]) => ({ name, value }));
}

function resolveSafetyIndices(safety: MgmtReviewInputs['safetyIncidents']) {
  if (!safety) return null;
  if (safety.indices) return safety.indices;
  const legacy = safety as {
    frequencyIndex?: number | null;
    severityIndex?: number | null;
  };
  if ('frequencyIndex' in legacy || 'severityIndex' in legacy) {
    return {
      frequencyIndex: legacy.frequencyIndex ?? null,
      severityIndex: legacy.severityIndex ?? null,
      incidenceIndex: null,
      hoursWorked: 0,
    };
  }
  return null;
}

export function InputsSummaryCharts({ inputs }: InputsSummaryChartsProps) {
  const ncData = [
    { name: 'Abiertas', value: inputs.nonConformities.open },
    { name: 'Cerradas', value: inputs.nonConformities.closed },
  ];
  const findingsData = toChartData(inputs.audits.findingsByType);
  const objectivesData = [
    { name: 'Logrados', value: inputs.objectives.achieved },
    { name: 'En riesgo', value: inputs.objectives.atRisk },
    {
      name: 'Otros',
      value: Math.max(
        0,
        inputs.objectives.total - inputs.objectives.achieved - inputs.objectives.atRisk,
      ),
    },
  ].filter((item) => item.value > 0);
  const indicatorsData = [
    { name: 'En meta', value: inputs.indicators.withinTarget },
    { name: 'Fuera de meta', value: inputs.indicators.offTarget },
    { name: 'Sin medición', value: inputs.indicators.missing },
  ].filter((item) => item.value > 0);
  const areaData = toChartData(inputs.nonConformities.byArea);
  const safetyIndices = resolveSafetyIndices(inputs.safetyIncidents);

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      <ChartCard title="No conformidades">
        <MiniPie data={ncData} />
      </ChartCard>
      <ChartCard title="Hallazgos de auditoría">
        <MiniBar data={findingsData} emptyLabel="Sin hallazgos en el período" />
      </ChartCard>
      <ChartCard title="Objetivos">
        <MiniPie data={objectivesData} />
      </ChartCard>
      <ChartCard title="Indicadores">
        <MiniPie data={indicatorsData} />
      </ChartCard>
      <ChartCard title="NC por área">
        <MiniBar data={areaData} emptyLabel="Sin NC en el período" />
      </ChartCard>
      <ChartCard title="Riesgos (MAP-03)">
        <ul className="text-sm space-y-2">
          <li>Abiertos: {inputs.risks.totalOpen}</li>
          <li>Críticos: {inputs.risks.critical}</li>
          <li>Altos: {inputs.risks.high}</li>
          <li>Altos abiertos (KPI): {inputs.risks.highOpenCount}</li>
          <li>En tratamiento: {inputs.risks.inTreatment}</li>
          <li>Con acción: {inputs.risks.withActionPercent}%</li>
          <li>Acciones vencidas: {inputs.risks.overdueActionsCount}</li>
          <li>Eficacia tratamiento: {inputs.risks.treatmentEffectivenessPercent}%</li>
        </ul>
      </ChartCard>
      <ChartCard title="Resumen adicional">
        <ul className="text-sm space-y-2">
          <li>
            Satisfacción promedio:{' '}
            {inputs.customerSatisfaction.avgScore != null
              ? inputs.customerSatisfaction.avgScore.toFixed(2)
              : '—'}
          </li>
          <li>Bajo umbral: {inputs.customerSatisfaction.belowThreshold}</li>
          <li>Capacitaciones: {inputs.training.sessions}</li>
          <li>Eficacia evaluada: {inputs.training.effectivenessEvaluated}</li>
          <li>Requisitos legales: {inputs.legalRequirements.total}</li>
          <li>No conformes: {inputs.legalRequirements.nonCompliant}</li>
          <li>Vencen en período: {inputs.legalRequirements.expiringInPeriod}</li>
          {inputs.soaCoverage && (
            <>
              <li>SoA — cobertura: {inputs.soaCoverage.implementedOrVerifiedPercent}%</li>
              <li>SoA — excluidos: {inputs.soaCoverage.excludedCount}</li>
            </>
          )}
          {inputs.safetyIncidents && (
            <>
              <li>Incidentes SST: {inputs.safetyIncidents.total ?? '—'}</li>
              <li>
                IF:{' '}
                {safetyIndices?.frequencyIndex != null
                  ? safetyIndices.frequencyIndex.toFixed(2)
                  : '—'}
              </li>
              <li>
                IG:{' '}
                {safetyIndices?.severityIndex != null
                  ? safetyIndices.severityIndex.toFixed(2)
                  : '—'}
              </li>
            </>
          )}
          {inputs.environmentalIndicators && (
            <>
              <li>Indicadores ambientales: {inputs.environmentalIndicators.total}</li>
              <li>En meta: {inputs.environmentalIndicators.withinTarget}</li>
              <li>Fuera de meta: {inputs.environmentalIndicators.offTarget}</li>
              <li>Sin medición: {inputs.environmentalIndicators.missing}</li>
            </>
          )}
          {inputs.environmentalAspects && (
            <>
              <li>Aspectos ambientales: {inputs.environmentalAspects.total}</li>
              <li>Significativos: {inputs.environmentalAspects.significantCount}</li>
              <li>Sin control: {inputs.environmentalAspects.withoutControlCount}</li>
            </>
          )}
          {inputs.changes && (
            <>
              <li>Solicitudes de cambio: {inputs.changes.total}</li>
              {Object.entries(inputs.changes.byStatus).map(([status, count]) => (
                <li key={status}>
                  {status}: {count}
                </li>
              ))}
            </>
          )}
        </ul>
      </ChartCard>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card border border-border rounded-lg p-4">
      <h3 className="font-semibold text-sm mb-3">{title}</h3>
      {children}
    </section>
  );
}

function MiniPie({ data }: { data: Array<{ name: string; value: number }> }) {
  if (data.length === 0 || data.every((d) => d.value === 0)) {
    return <EmptyChart />;
  }
  return (
    <div className="h-[180px]">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60}>
            {data.map((_, index) => (
              <Cell key={data[index].name} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <SpanishTooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function MiniBar({
  data,
  emptyLabel,
}: {
  data: Array<{ name: string; value: number }>;
  emptyLabel: string;
}) {
  if (data.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <div className="h-[180px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis allowDecimals={false} />
          <SpanishTooltip />
          <Bar dataKey="value" name="Cantidad" fill="#2563eb" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="h-[180px] flex items-center justify-center text-sm text-muted-foreground">
      Sin datos
    </div>
  );
}
